/// <reference types="@webgpu/types" />
import type { LetterboxedFrame } from "./pose";
import { windowHomography, type SourceWindow, type ViewRect } from "../shared/geometry";
import { log, warn } from "../shared/debug";
import { pixelSize, type PixelSource } from "./camera-frame";
import { thumbnailIndices } from "./swap-thumbnail";
import { letterboxGeometry, letterboxSurface } from "./letterbox-canvas";
export { letterboxGeometry } from "./letterbox-canvas";

function shader(external: boolean): string {
  return `
struct Params { raster: vec4<f32>, size: vec4<f32>, area: vec4<f32>,
                h0: vec4<f32>, h1: vec4<f32>, h2: vec4<f32>, taps: vec4<f32> }
@group(0) @binding(0) var source: ${external ? "texture_external" : "texture_2d<f32>"};
@group(0) @binding(1) var linear: sampler;
@group(0) @binding(2) var<storage, read_write> input: array<f32>;
@group(0) @binding(3) var<uniform> p: Params;
@compute @workgroup_size(8, 8)
fn main(@builtin(global_invocation_id) id: vec3<u32>) {
  let width = u32(p.raster.x); let height = u32(p.raster.y);
  if (id.x >= width || id.y >= height) { return; }
  let local = vec2<f32>(id.xy) - p.raster.zw;
  var rgb = vec3<f32>(114.0 / 255.0);
  if (all(local >= vec2<f32>(0.0)) && all(local < p.size.xy)) {
    var sum = vec3<f32>(0.0);
    for (var y = 0u; y < u32(p.taps.y); y++) {
      for (var x = 0u; x < u32(p.taps.x); x++) {
        let subpixel = (vec2<f32>(f32(x), f32(y)) + 0.5) / p.taps.xy;
        let point = (local + subpixel) / p.size.xy * p.area.zw + p.area.xy;
        let v = vec3<f32>(point, 1.0);
        let sensor = vec2<f32>(dot(p.h0.xyz, v), dot(p.h1.xyz, v)) / dot(p.h2.xyz, v);
        let uv = sensor / p.size.zw;
        sum += ${external ? "textureSampleBaseClampToEdge(source, linear, uv)" : "textureSampleLevel(source, linear, uv, 0.0)"}.rgb;
      }
    }
    rgb = floor(clamp(sum / (p.taps.x * p.taps.y), vec3<f32>(0.0), vec3<f32>(1.0)) * 255.0 + 0.5) / 255.0;
  }
  let plane = width * height; let at = id.y * width + id.x;
  input[at] = rgb.r; input[plane + at] = rgb.g; input[2u * plane + at] = rgb.b;
}`;
}

const THUMB_SHADER = `
@group(0) @binding(0) var<storage, read> input: array<f32>;
@group(0) @binding(1) var<storage, read_write> output: array<f32>;
@group(0) @binding(2) var<storage, read> indices: array<u32>;
@compute @workgroup_size(8, 8)
fn main(@builtin(global_invocation_id) id: vec3<u32>) {
  if (id.x >= 16u || id.y >= 16u) { return; }
  let plane = PLANE; let at = indices[id.y * 16u + id.x];
  output[id.y * 16u + id.x] = dot(vec3<f32>(input[at], input[plane + at], input[2u * plane + at]),
                                 vec3<f32>(0.299, 0.587, 0.114)) * 255.0;
}`;

/**
 * Video → planar RGB on ORT's own device and input buffer. Import and submission happen
 * in one task: an external video texture expires across task boundaries. A copied GPU
 * texture is the fallback; neither route reads the detector raster onto the CPU.
 * https://www.w3.org/TR/webgpu/#dom-gpudevice-importexternaltexture
 */
export class GpuLetterbox {
  lastPath: "external" | "copy" | "canvas" | null = null;
  private readonly colorCanvas = letterboxSurface();
  private copied: GPUTexture | null = null;
  private copiedSize = "";
  private external = true;
  private disabled = false;
  private disposed = false;
  private readonly params: GPUBuffer;
  private readonly thumbParams: GPUBuffer;
  private readonly thumbOutput: GPUBuffer;
  private readonly thumbReadback: GPUBuffer;
  private readonly sampler: GPUSampler;
  private readonly empty = new Uint8ClampedArray(0);

  private constructor(
    private readonly device: GPUDevice,
    private readonly input: GPUBuffer,
    private readonly width: number,
    private readonly height: number,
    private readonly externalPipeline: GPUComputePipeline | null,
    private readonly copyPipeline: GPUComputePipeline,
    private readonly thumbPipeline: GPUComputePipeline,
  ) {
    this.params = device.createBuffer({
      size: 112,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
    this.thumbParams = device.createBuffer({
      size: 1024,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.thumbOutput = device.createBuffer({
      size: 1024,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
    });
    this.thumbReadback = device.createBuffer({
      size: 1024,
      usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
    });
    this.sampler = device.createSampler({ minFilter: "linear", magFilter: "linear" });
    this.external = externalPipeline !== null;
    void device.lost.then(() => {
      this.disabled = true;
    });
  }

  static async create(
    device: GPUDevice,
    input: GPUBuffer,
    width: number,
    height: number,
    preferExternal = true,
  ): Promise<GpuLetterbox | null> {
    const pipeline = (code: string) =>
      device.createComputePipelineAsync({
        layout: "auto",
        compute: { module: device.createShaderModule({ code }), entryPoint: "main" },
      });
    device.pushErrorScope("validation");
    let scopeOpen = true;
    try {
      const copy = await pipeline(shader(false));
      const thumb = await pipeline(THUMB_SHADER.replace("PLANE", `${width * height}u`));
      let external: GPUComputePipeline | null = null;
      // External textures are optional on older implementations. The copy shader is
      // built first so its absence never prevents the existing CPU input path booting.
      if (preferExternal && typeof device.importExternalTexture === "function") {
        device.pushErrorScope("validation");
        try {
          external = await pipeline(shader(true));
        } catch {
          /* use the copy path */
        }
        if (await device.popErrorScope()) external = null;
      }
      scopeOpen = false;
      const error = await device.popErrorScope();
      if (error) throw new Error(error.message);
      return new GpuLetterbox(device, input, width, height, external, copy, thumb);
    } catch (error) {
      if (scopeOpen) await device.popErrorScope().catch(() => null);
      warn("detect", "WebGPU letterbox unavailable — retaining raster input", error);
      return null;
    }
  }

  async prepare(
    video: PixelSource,
    window: SourceWindow,
    area: ViewRect,
  ): Promise<LetterboxedFrame | null> {
    const sensor = pixelSize(video);
    if (
      this.disabled ||
      this.disposed ||
      !sensor.width ||
      !sensor.height ||
      area.w <= 0 ||
      area.h <= 0
    )
      return null;
    const geometry = letterboxGeometry(this.width, this.height, area);
    // HDR/unknown transfer curves do not receive Canvas' SDR tone mapping through
    // WebGPU video imports. Let the browser convert them on a model-size GPU canvas,
    // then copy that texture: still no getImageData, planar CPU pack or input upload.
    const transfer = "colorSpace" in video ? video.colorSpace.transfer : null;
    const convertColor = !transfer || !["bt709", "iec61966-2-1", "smpte170m"].includes(transfer);
    const converted = convertColor
      ? this.colorCanvas.draw(video, window, this.width, this.height, area)
      : null;
    if (convertColor && !converted) return null;
    const textureSource = converted?.canvas ?? video;
    const textureSize = converted ? { width: this.width, height: this.height } : sensor;
    const inputArea = converted ? { x: 0, y: 0, w: this.width, h: this.height } : area;
    const inputGeometry = converted
      ? letterboxGeometry(this.width, this.height, inputArea)
      : geometry;
    const h = converted ? [1, 0, 0, 0, 1, 0, 0, 0, 1] : windowHomography(window);
    const parameters = new Float32Array([
      this.width,
      this.height,
      inputGeometry.left,
      inputGeometry.top,
      inputGeometry.scaledWidth,
      inputGeometry.scaledHeight,
      textureSize.width,
      textureSize.height,
      inputArea.x,
      inputArea.y,
      inputArea.w,
      inputArea.h,
      h[0],
      h[1],
      h[2],
      0,
      h[3],
      h[4],
      h[5],
      0,
      h[6],
      h[7],
      h[8],
      0,
      Math.min(8, Math.max(1, Math.ceil(inputArea.w / inputGeometry.scaledWidth))),
      Math.min(8, Math.max(1, Math.ceil(inputArea.h / inputGeometry.scaledHeight))),
      0,
      0,
    ]);
    for (let attempt = 0; attempt < 2; attempt++) {
      const external = this.external && !converted;
      this.device.pushErrorScope("validation");
      let failure: unknown = null;
      try {
        let source: GPUExternalTexture | GPUTextureView;
        if (external) source = this.device.importExternalTexture({ source: video });
        else {
          const size = `${textureSize.width}x${textureSize.height}`;
          if (size !== this.copiedSize) {
            this.copied?.destroy();
            this.copied = this.device.createTexture({
              size: [textureSize.width, textureSize.height],
              format: "rgba8unorm",
              usage:
                GPUTextureUsage.COPY_DST |
                GPUTextureUsage.TEXTURE_BINDING |
                GPUTextureUsage.RENDER_ATTACHMENT,
            });
            this.copiedSize = size;
          }
          this.device.queue.copyExternalImageToTexture(
            { source: textureSource },
            { texture: this.copied! },
            [textureSize.width, textureSize.height],
          );
          source = this.copied!.createView();
        }
        const pipeline = external ? this.externalPipeline! : this.copyPipeline;
        this.device.queue.writeBuffer(this.params, 0, parameters);
        const group = this.device.createBindGroup({
          layout: pipeline.getBindGroupLayout(0),
          entries: [
            { binding: 0, resource: source },
            { binding: 1, resource: this.sampler },
            { binding: 2, resource: { buffer: this.input } },
            { binding: 3, resource: { buffer: this.params } },
          ],
        });
        const encoder = this.device.createCommandEncoder({ label: "detector video letterbox" });
        const pass = encoder.beginComputePass();
        pass.setPipeline(pipeline);
        pass.setBindGroup(0, group);
        pass.dispatchWorkgroups(Math.ceil(this.width / 8), Math.ceil(this.height / 8));
        pass.end();
        this.device.queue.submit([encoder.finish()]);
      } catch (error) {
        failure = error;
      }
      const validationError = await this.device.popErrorScope();
      failure ??= validationError;
      if (!failure) {
        const path = converted ? "canvas" : external ? "external" : "copy";
        if (path !== this.lastPath)
          log("detect", `WebGPU letterbox: ${path} texture → planar input; no raster readback`);
        this.lastPath = path;
        return { data: this.empty, ...geometry, gpu: true };
      }
      if (external) {
        this.external = false;
        log("detect", "external video import unavailable — using WebGPU texture copy");
      } else {
        this.disabled = true;
        warn("detect", "WebGPU video sampling failed — retaining raster input", failure);
        return null;
      }
    }
    return null;
  }

  /** Only 1 KB comes back, and only when a latch needs proof or a read is spent. */
  async thumbnail(
    frame: LetterboxedFrame,
    quad: Float32Array,
    area: ViewRect,
  ): Promise<Float32Array> {
    const values = thumbnailIndices(frame, quad, area, this.width, this.height);
    this.device.queue.writeBuffer(this.thumbParams, 0, values);
    const group = this.device.createBindGroup({
      layout: this.thumbPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.input } },
        { binding: 1, resource: { buffer: this.thumbOutput } },
        { binding: 2, resource: { buffer: this.thumbParams } },
      ],
    });
    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(this.thumbPipeline);
    pass.setBindGroup(0, group);
    pass.dispatchWorkgroups(2, 2);
    pass.end();
    encoder.copyBufferToBuffer(this.thumbOutput, 0, this.thumbReadback, 0, 1024);
    this.device.queue.submit([encoder.finish()]);
    await this.thumbReadback.mapAsync(GPUMapMode.READ);
    try {
      const out = new Float32Array(this.thumbReadback.getMappedRange().slice(0));
      const mean = out.reduce((sum, value) => sum + value, 0) / out.length;
      for (let i = 0; i < out.length; i++) out[i] -= mean;
      return out;
    } finally {
      this.thumbReadback.unmap();
    }
  }

  /** Called between camera sessions; ORT still owns its reusable inference buffers. */
  releaseVideo(): void {
    this.copied?.destroy();
    this.copied = null;
    this.copiedSize = "";
    this.colorCanvas.release();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.releaseVideo();
    for (const buffer of [this.params, this.thumbParams, this.thumbOutput, this.thumbReadback])
      buffer.destroy();
  }
}
